import React, { useMemo } from 'react'
import { useCurrent, useFetch } from '../hooks'
import { Link, useParams } from 'react-router-dom'
import MonthlyLineChart from '../components/dashborad/MonthlyLineChart'
import { FaPhoneAlt, FaEnvelope, FaPlus, FaTicketAlt } from "react-icons/fa"
import { useTranslation } from 'react-i18next'
import { getStatusAppearance, normalizeSupportSettings } from '../constants/ticket'
import MwSpinnerButton from '../components/ui/MwSpinnerButton'

const TotalCard = ({ label, subLabel, loading, data, trend, trendUp = true, icon, iconBg, iconColor, barColor, to }) => {
    const content = (
        <div className="bg-white border border-slate-100 hover:border-[#f5b800]/35 transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(8,32,63,.08)] rounded-[18px] p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center flex-shrink-0" style={{ background: iconBg, color: iconColor }}>
                    {icon}
                </div>
                {trend !== undefined && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: iconBg, color: iconColor }}>
                        {trendUp
                            ? <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 7l3-4 3 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            : <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3l3 4 3-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        }
                        {trend}
                    </span>
                )}
            </div>
            <div>
                <div className="text-[30px] font-bold leading-none text-[#08203f]">
                    {loading ? <div className="h-8 flex items-center"><MwSpinnerButton withLabel={false} /></div> : data}
                </div>
                <div className="text-xs text-slate-600 font-semibold mt-1.5">{label}</div>
            </div>
            <div className="flex flex-col gap-1.5">
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: loading ? '0%' : '100%', background: barColor }} />
                </div>
                <div className="flex justify-between text-[11px] text-slate-500 font-medium">
                    <span>{subLabel}</span>
                    <span style={{ color: barColor, fontWeight: 500 }}>100%</span>
                </div>
            </div>
        </div>
    );
    return to ? <Link to={to} className="block">{content}</Link> : content;
};

const StatusStrip = ({ statuses, statusCounts, allTicketCount, loading, isAr, tenantUsername }) => {
    if (loading) {
        return (
            <div className="bg-white border border-[#f5b800]/20 rounded-[16px] px-5 py-3.5 flex items-center gap-3">
                <MwSpinnerButton withLabel={false} />
            </div>
        );
    }

    return (
        <div className="bg-white border border-[#f5b800]/20 rounded-[16px] px-4 py-3 w-full min-w-0 max-w-full overflow-x-auto shadow-[0_10px_24px_rgba(8,32,63,.04)]">
            <div className="flex items-center flex-nowrap whitespace-nowrap w-full min-w-max justify-between">
                <Link
                    to={`/${tenantUsername}/tickets`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all hover:scale-[1.04] active:scale-[0.97] select-none flex-shrink-0"
                    style={{
                        background: '#fff5d6',
                        color: '#08203f',
                        border: '1.5px solid #f5b80066',
                    }}
                >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#f5b800' }} />
                    <span>{isAr ? 'كل الحالات' : 'All statuses'}</span>
                    <span
                        className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-black"
                        style={{ background: '#f5b80033', color: '#08203f' }}
                    >
                        {Number(allTicketCount || 0)}
                    </span>
                </Link>
                {statuses.map((status) => {
                    const id = String(status?._id || '');
                    const count = Number(statusCounts?.[id] || 0);
                    const label = isAr ? status.labelAr : status.labelEn;
                    const appearance = status._appearance || { color: '#08203f', bg: '#fff5d6', dot: '#f5b800' };

                    return (
                        <Link
                            key={id}
                            to={`/${tenantUsername}/tickets?status=${encodeURIComponent(id)}&period=all`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all hover:scale-[1.04] active:scale-[0.97] select-none flex-shrink-0"
                            style={{
                                background: appearance.bg,
                                color: appearance.color,
                                border: `1.5px solid ${appearance.dot}22`,
                            }}
                        >
                            <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ background: appearance.dot }}
                            />
                            <span>{label}</span>
                            <span
                                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-black"
                                style={{ background: appearance.dot + '22', color: appearance.color }}
                            >
                                {count}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

const DashBoard = () => {
    const { currentLangId } = useCurrent();
    const [t] = useTranslation('global');
    const tenantUsername = useParams()?.tenant;
    const isAr = currentLangId === 'ar';

    const { data: statsResult, loading: loadingTickets } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/stats`);
    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const { data: statusesResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses/admin`);
    const stats = statsResult?.data || {};
    const allTicketCount = Number(stats?.total || 0);
    const monthlyList = Array.isArray(stats?.monthlyList) ? stats.monthlyList : Array.from({ length: 12 }, () => 0);
    const statusCounts = stats?.statusCounts || {};
    const thisMonthTicketCount = Number(stats?.thisMonth || 0);
    const thisYearTicketCount = Number(stats?.thisYear || 0);
    const lastYearTicketCount = Number(stats?.lastYear || 0);

    const supportSettingsBase = useMemo(() => normalizeSupportSettings(supportSettingsResult), [supportSettingsResult]);
    const supportSettings = useMemo(() => ({
        ...supportSettingsBase,
        statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
    }), [supportSettingsBase, statusesResult]);

    const activeStatuses = useMemo(
        () => (supportSettings?.statuses || []).filter((item) => item?.isActive !== false).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        [supportSettings?.statuses]
    );

    const enrichedStatuses = useMemo(
        () => activeStatuses.map(status => {
            const id = String(status?._id || '');
            return { ...status, _appearance: getStatusAppearance(supportSettings?.statuses, id) };
        }),
        [activeStatuses, supportSettings?.statuses]
    );

    return (
        <div className="flex flex-col gap-6 pb-10">
            <div className="bg-white border border-[#f5b800]/20 rounded-[20px] relative overflow-hidden shadow-[0_16px_40px_rgba(8,32,63,.06)]">
                <div className="absolute top-0 left-9 right-9 h-[3px] bg-[#f5b800] rounded-b-sm" />
                <div className="absolute -top-16 -end-14 w-44 h-44 rounded-full bg-[#fff5d6]" />
                <div className="absolute bottom-0 start-0 w-28 h-28 bg-[#08203f] opacity-[0.04] rounded-se-[48px]" />
                <div
                    className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
                    style={{
                        opacity: 0.03,
                        backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                    }}
                />

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6 p-8 md:p-10 flex-wrap">
                    <div className="flex flex-col gap-3.5 max-w-md text-center md:text-start">
                        <div className="inline-flex items-center gap-2 self-center md:self-start bg-[#fff5d6] border border-[#f5b800]/30 rounded-full px-3.5 py-1.5 w-fit">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#f5b800] flex-shrink-0" />
                            <span className="text-[11px] text-[#08203f] font-semibold">
                                {isAr ? 'نظام الدعم الفني' : 'Support System'}
                            </span>
                        </div>

                        <h1 className="text-[26px] md:text-[28px] font-bold leading-snug text-[#08203f]">
                            {t('support.dashboard.title')}
                        </h1>

                        <p className="text-sm text-slate-600 font-medium leading-relaxed">
                            {isAr ? 'لوحة تحكم إدارة الدعم الفني' : 'Support Admin Dashboard'}
                        </p>
                    </div>

                    <div className="flex flex-col gap-3 w-full md:w-auto md:min-w-[240px]">
                        <Link to={`/${tenantUsername}/tickets/add`} className="w-full">
                            <button className="w-full bg-[#f5b800] hover:bg-[#d99d00] active:scale-[0.98] transition-all text-[#08203f] px-6 py-2.5 rounded-xl text-sm font-black flex items-center justify-center gap-2 shadow-[0_12px_24px_rgba(245,184,0,.26)]">
                                <FaPlus size={11} />
                                {t('support.dashboard.newTicket')}
                            </button>
                        </Link>

                        <div className="border border-slate-100 rounded-xl overflow-hidden">
                            <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                                <div className="w-7 h-7 rounded-lg bg-[#fff5d6] flex items-center justify-center flex-shrink-0">
                                    <FaPhoneAlt size={11} className="text-[#08203f]" />
                                </div>
                                <span className="text-xs text-slate-700 font-semibold" dir="ltr">
                                    {supportSettings?.contact?.phone || '-'}
                                </span>
                            </div>
                            <div className="h-px bg-slate-100" />
                            <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                                <div className="w-7 h-7 rounded-lg bg-[#fff5d6] flex items-center justify-center flex-shrink-0">
                                    <FaEnvelope size={11} className="text-[#08203f]" />
                                </div>
                                <span className="text-xs text-slate-700 font-semibold">
                                    {supportSettings?.contact?.email || '-'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col gap-5">
                <div className="flex items-center gap-2.5">
                    <span className="w-0.5 h-4 rounded-sm bg-[#f5b800]" />
                    <h2 className="text-[13px] font-semibold text-[#08203f]">
                        {isAr ? 'نظرة عامة على النشاط' : 'Activity Overview'}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <TotalCard
                        label={isAr ? 'تذاكر الشهر الحالي' : 'Tickets this month'}
                        subLabel={isAr ? 'الشهر الحالي' : 'This month'}
                        loading={loadingTickets}
                        data={thisMonthTicketCount}
                        icon={<FaTicketAlt size={17} />}
                        iconBg="#fff5d6"
                        iconColor="#08203f"
                        barColor="#f5b800"
                        to={`/${tenantUsername}/tickets`}
                    />
                    <TotalCard
                        label={isAr ? 'تذاكر السنة الحالية' : 'Tickets this year'}
                        subLabel={isAr ? 'السنة الحالية' : 'This year'}
                        loading={loadingTickets}
                        data={thisYearTicketCount}
                        icon={<FaTicketAlt size={17} />}
                        iconBg="#EAF7FF"
                        iconColor="#0284c7"
                        barColor="#0284c7"
                        to={`/${tenantUsername}/tickets`}
                    />
                    <TotalCard
                        label={isAr ? 'تذاكر العام الماضي' : 'Tickets last year'}
                        subLabel={isAr ? 'العام الماضي' : 'Last year'}
                        loading={loadingTickets}
                        data={lastYearTicketCount}
                        icon={<FaTicketAlt size={17} />}
                        iconBg="#FCE7F3"
                        iconColor="#be185d"
                        barColor="#be185d"
                        to={`/${tenantUsername}/tickets`}
                    />
                </div>

                <StatusStrip
                    statuses={enrichedStatuses}
                    statusCounts={statusCounts}
                    allTicketCount={allTicketCount}
                    loading={loadingTickets}
                    isAr={isAr}
                    tenantUsername={tenantUsername}
                />

                <div className="bg-white border border-[#f5b800]/20 rounded-[18px] p-6 md:p-8 shadow-[0_12px_30px_rgba(8,32,63,.05)]">
                    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <h2 className="text-[15px] font-bold text-[#08203f]">
                                {t('support.dashboard.yearlyTickets')}
                            </h2>
                            <p className="text-xs text-slate-600 font-medium mt-1">
                                {isAr
                                    ? 'إحصائيات تذاكر الدعم خلال الـ 12 شهراً الماضية'
                                    : 'Support tickets over the last 12 months'}
                            </p>
                        </div>

                        {!loadingTickets && monthlyList.some(v => v > 0) && (
                            <div className="flex items-center gap-2 bg-[#fff5d6] border border-[#f5b800]/30 rounded-full px-3.5 py-1.5">
                                <span className="w-2 h-2 rounded-full bg-[#f5b800]" />
                                <span className="text-[11px] text-[#08203f] font-semibold">
                                    {isAr ? 'عدد التذاكر' : 'Tickets'}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="h-[280px] md:h-[360px]">
                        <MonthlyLineChart
                            tenantUsername={tenantUsername}
                            loading={loadingTickets}
                            monthlyList={monthlyList}
                            label={t('support.dashboard.yearlyTickets')}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DashBoard
